import supabase from '../supabaseClient';

/**
 * Fetches recipient data (full objects) based on filtering criteria
 * @param {string} recipientType - 'dugsi', 'shift', or 'class'
 * @param {Array} selectedShifts - Array of shift names
 * @param {Array} selectedClasses - Array of class IDs
 * @param {Object} roles - Object with includeParents and includeTeachers booleans
 * @returns {Promise<Object>} Object containing parents and teachers arrays with full data
 */
export const fetchRecipientData = async (recipientType, selectedShifts, selectedClasses, roles) => {
  let parents = [];
  let teachers = [];
  
  console.group('Fetching Recipient Data');
  console.log('Parameters:', { recipientType, selectedShifts, selectedClasses, roles });
  
  try {
    // Fetch parent data if selected
    if (roles.includeParents) {
      console.log('Fetching parent data...');
      
      // Get students directly based on recipient type
      let studentsQuery = supabase
        .from('students')
        .select('guardian_id')
        .not('guardian_id', 'is', null)
        .eq('is_active', true);

      if (recipientType === 'shift') {
        const { data: shiftClasses, error: classesError } = await supabase
          .from('classes')
          .select('id')
          .in('shift', selectedShifts);

        if (classesError) throw classesError;
        if (shiftClasses && shiftClasses.length > 0) {
          const classIds = shiftClasses.map(c => c.id);
          studentsQuery = studentsQuery.overlaps('class_ids', classIds);
        }
      } else if (recipientType === 'class') {
        studentsQuery = studentsQuery.overlaps('class_ids', selectedClasses);
      }

      const { data: students, error: studentsError } = await studentsQuery;
      if (studentsError) throw studentsError;
      
      console.log('Found students:', students);
      
      if (students && students.length > 0) {
        const guardianIds = [...new Set(students.map(s => s.guardian_id))];
        
        // Get guardians' full data
        const { data: guardians, error: guardiansError } = await supabase
          .from('guardians')
          .select('id, first_name, last_name, email, phone, sms_enabled, message_notifications, last_active')
          .in('id', guardianIds)
          .not('phone', 'is', null)
          .eq('sms_enabled', true);

        if (guardiansError) throw guardiansError;
        
        console.log('Found guardians:', guardians);
        if (guardians) {
          parents = guardians;
        }
      }
    }

    // Fetch teacher data if selected
    if (roles.includeTeachers) {
      console.log('Fetching teacher data...');
      
      if (recipientType === 'class') {
        // For class, get teachers assigned to selected classes
        const { data: classData, error: classError } = await supabase
          .from('classes')
          .select(`
            teacher_id,
            teachers (
              id,
              first_name,
              last_name,
              email,
              phone,
              gender,
              join_date,
              last_active,
              message_notifications,
              is_active
            )
          `)
          .in('id', selectedClasses)
          .not('teacher_id', 'is', null);
        
        if (classError) throw classError;
        
        if (classData) {
          const teacherData = classData
            .filter(c => c.teachers?.phone && c.teachers?.is_active !== false)
            .map(c => c.teachers);
          teachers = teacherData;
        }
      } else if (recipientType === 'shift') {
        // For shift, get teachers who teach classes in these shifts
        const { data: classTeachers, error: classTeachersError } = await supabase
          .from('classes')
          .select(`
            teacher_id,
            teachers (
              id,
              first_name,
              last_name,
              email,
              phone,
              gender,
              join_date,
              last_active,
              message_notifications,
              is_active
            )
          `)
          .in('shift', selectedShifts)
          .not('teacher_id', 'is', null);
        
        if (classTeachersError) throw classTeachersError;
        
        if (classTeachers) {
          const teacherData = classTeachers
            .filter(ct => ct.teachers?.phone && ct.teachers?.is_active !== false)
            .map(ct => ct.teachers);
          teachers = teacherData;
        }
      } else {
        // For entire Dugsi, get all active teachers
        const { data: allTeachers, error: teachersError } = await supabase
          .from('teachers')
          .select('id, first_name, last_name, email, phone, gender, join_date, last_active, message_notifications')
          .eq('is_active', true)
          .not('phone', 'is', null);
          
        if (teachersError) throw teachersError;
        if (allTeachers) {
          teachers = allTeachers;
        }
      }
    }

    console.log('Final data:', { parents: parents.length, teachers: teachers.length });
    console.groupEnd();

    // Remove duplicates based on ID
    const uniqueParents = parents.filter((parent, index, self) => 
      index === self.findIndex(p => p.id === parent.id)
    );
    const uniqueTeachers = teachers.filter((teacher, index, self) => 
      index === self.findIndex(t => t.id === teacher.id)
    );

    return {
      parents: uniqueParents,
      teachers: uniqueTeachers
    };
  } catch (error) {
    console.error('Error fetching recipient data:', error);
    console.groupEnd();
    throw new Error(`Failed to fetch recipient data: ${error.message}`);
  }
};

/**
 * Extracts phone numbers from recipient data for SMS functionality
 * @param {Object} recipientData - Object with parents and teachers arrays
 * @returns {Array} Array of phone numbers
 */
export const extractPhoneNumbers = (recipientData) => {
  const phoneNumbers = [];
  
  if (recipientData.parents) {
    phoneNumbers.push(...recipientData.parents.map(p => p.phone).filter(Boolean));
  }
  
  if (recipientData.teachers) {
    phoneNumbers.push(...recipientData.teachers.map(t => t.phone).filter(Boolean));
  }
  
  // Remove duplicates
  return [...new Set(phoneNumbers)];
};

/**
 * Converts array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} fields - Array of field names to include in CSV
 * @param {Object} fieldLabels - Object mapping field names to display labels
 * @returns {string} CSV string
 */
export const convertToCSV = (data, fields, fieldLabels = {}) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Create header row using labels or field names
  const headers = fields.map(field => fieldLabels[field] || field);
  const csvRows = [headers.join(',')];

  // Convert each row to CSV
  data.forEach(item => {
    const row = fields.map(field => {
      let value = item[field] || '';
      
      // Handle different data types
      if (value === null || value === undefined) {
        value = '';
      } else if (typeof value === 'string') {
        // Escape quotes and wrap in quotes if contains comma, quote, or newline
        if (value.includes(',') || value.includes('"') || value.includes('\n')) {
          value = `"${value.replace(/"/g, '""')}"`;
        }
      } else if (value instanceof Date) {
        value = value.toLocaleDateString();
      } else {
        value = String(value);
      }
      
      return value;
    });
    csvRows.push(row.join(','));
  });

  return csvRows.join('\n');
};

/**
 * Downloads CSV data as a file
 * @param {string} csvContent - CSV content string
 * @param {string} filename - Name for the downloaded file
 */
export const downloadCSV = (csvContent, filename) => {
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Generates filename for export based on parameters
 * @param {string} exportType - 'teachers', 'parents', or 'combined'
 * @param {string} recipientType - 'dugsi', 'shift', or 'class'
 * @param {Array} selectedShifts - Array of shift names
 * @param {Array} selectedClasses - Array of class IDs
 * @param {Array} classes - Array of all classes for name lookup
 * @returns {string} Generated filename
 */
export const generateExportFilename = (exportType, recipientType, selectedShifts, selectedClasses, classes) => {
  const timestamp = new Date().toISOString().split('T')[0]; // YYYY-MM-DD format
  
  let scope = '';
  if (recipientType === 'dugsi') {
    scope = 'entire_dugsi';
  } else if (recipientType === 'shift') {
    scope = `shift_${selectedShifts.join('_').replace(/\s+/g, '_').toLowerCase()}`;
  } else if (recipientType === 'class') {
    const selectedClassNames = classes
      .filter(cls => selectedClasses.includes(cls.id))
      .map(cls => cls.name.replace(/\s+/g, '_'))
      .join('_');
    scope = `class_${selectedClassNames}`;
  }
  
  return `${exportType}_${scope}_${timestamp}.csv`;
};

/**
 * Main export function that handles the complete export process
 * @param {string} exportType - 'teachers', 'parents', or 'combined'
 * @param {string} recipientType - 'dugsi', 'shift', or 'class'
 * @param {Array} selectedShifts - Array of shift names
 * @param {Array} selectedClasses - Array of class IDs
 * @param {Array} classes - Array of all classes for name lookup
 * @returns {Promise<Object>} Result object with success status and details
 */
export const exportData = async (exportType, recipientType, selectedShifts, selectedClasses, classes) => {
  try {
    // Determine which roles to include based on export type
    const roles = {
      includeParents: exportType === 'parents' || exportType === 'combined',
      includeTeachers: exportType === 'teachers' || exportType === 'combined'
    };

    // Fetch the data
    const recipientData = await fetchRecipientData(recipientType, selectedShifts, selectedClasses, roles);
    
    // Define fields to export
    const exportFields = ['first_name', 'last_name', 'phone', 'email'];
    const fieldLabels = {
      first_name: 'First Name',
      last_name: 'Last Name',
      phone: 'Phone Number',
      email: 'Email'
    };

    let csvContent = '';
    let totalRecords = 0;

    if (exportType === 'teachers' && recipientData.teachers.length > 0) {
      csvContent = convertToCSV(recipientData.teachers, exportFields, fieldLabels);
      totalRecords = recipientData.teachers.length;
    } else if (exportType === 'parents' && recipientData.parents.length > 0) {
      csvContent = convertToCSV(recipientData.parents, exportFields, fieldLabels);
      totalRecords = recipientData.parents.length;
    } else if (exportType === 'combined') {
      // Combine both with a type indicator
      const combinedData = [
        ...recipientData.teachers.map(t => ({ ...t, type: 'Teacher' })),
        ...recipientData.parents.map(p => ({ ...p, type: 'Parent' }))
      ];
      
      if (combinedData.length > 0) {
        const combinedFields = ['type', ...exportFields];
        const combinedLabels = { type: 'Type', ...fieldLabels };
        csvContent = convertToCSV(combinedData, combinedFields, combinedLabels);
        totalRecords = combinedData.length;
      }
    }

    if (!csvContent) {
      return {
        success: false,
        error: 'No data found for the selected criteria'
      };
    }

    // Generate filename and download
    const filename = generateExportFilename(exportType, recipientType, selectedShifts, selectedClasses, classes);
    downloadCSV(csvContent, filename);

    return {
      success: true,
      recordCount: totalRecords,
      filename
    };
  } catch (error) {
    console.error('Export error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
